import { useEffect, useState } from 'react';
import {
  AppEmptyState,
  AppFilePicker,
  AppFormDialog,
  AppMenuSheet,
  AppPickerRow,
  AppScreenScaffold,
  AppTextField,
  AppTopBar,
} from '@core/designsystem';
import { ScanEditor } from './ScanEditor';
import { processShot, type Shot } from './scanning';
import type { QuickScanCase, QuickScanState } from './useQuickScan';

/**
 * Быстрый скан без дела — «Структура» §11.
 *
 * Смотреть тут не на что, пока страница не снята: экран занят правкой границ,
 * а следом — вопросом, куда её положить. Если кадра нет (сюда пришли по адресу
 * или отменили съёмку), экран не молчит, а предлагает снять — молчащий экран
 * читается как поломка, и на Android это уже было поймано на устройстве.
 */
export function QuickScanScreen({
  photo,
  state,
  onShot,
  onSave,
  onBack,
}: {
  /** Снятый кадр. Приходит из «плюса»: камера открывается прямо из нажатия. */
  photo: File | null;
  state: QuickScanState;
  onShot: (photos: File[]) => void;
  onSave: (choice: { caseId: string | null; newCaseTitle: string; documentTitle: string; page: Blob }) => void;
  onBack: () => void;
}) {
  const [shot, setShot] = useState<Shot | null>(null);
  const [page, setPage] = useState<Blob | null>(null);
  const [flattening, setFlattening] = useState(false);

  // Кадр разбирается один раз на снимок: обработка идёт эффектом, а не по ходу
  // отрисовки — иначе она запускается дважды и подменяет уже поправленные углы.
  useEffect(() => {
    if (!photo) return;

    let dropped = false;
    setShot(null);
    setPage(null);

    void processShot(photo).then((ready) => {
      if (!dropped) setShot(ready);
    });

    return () => {
      dropped = true;
    };
  }, [photo]);

  if (page) {
    return (
      <WhereToSaveDialog
        cases={state.cases}
        isSaving={state.isSaving}
        onDismiss={onBack}
        onSave={(choice) => onSave({ ...choice, page })}
      />
    );
  }

  if (shot) {
    return (
      <ScanEditor
        photo={shot.photo}
        corners={shot.corners}
        detected={shot.detected}
        busy={flattening}
        onCancel={onBack}
        onConfirm={(corners) => {
          setFlattening(true);
          void shot
            .flatten(corners)
            .then(setPage)
            .finally(() => setFlattening(false));
        }}
      />
    );
  }

  return (
    <AppScreenScaffold topBar={<AppTopBar title="Быстрый скан" onBack={onBack} />}>
      <AppEmptyState
        icon="document_scanner"
        text={
          state.errorMessage ??
          (photo ? 'Разбираем снимок…' : 'Снимите страницу — дело выберете после')
        }
        action={
          photo && !state.errorMessage ? undefined : (
            <AppFilePicker
              label="Снять страницу"
              accept="image/*"
              capture="environment"
              onFiles={onShot}
            />
          )
        }
      />
    </AppScreenScaffold>
  );
}

/**
 * Куда положить свежий скан.
 *
 * «Новое дело» первым: снимают чаще всего то, для чего дела ещё нет. Название
 * документа необязательно — придумывать имя в коридоре суда некогда.
 */
function WhereToSaveDialog({
  cases,
  isSaving,
  onDismiss,
  onSave,
}: {
  cases: QuickScanCase[];
  isSaving: boolean;
  onDismiss: () => void;
  onSave: (choice: { caseId: string | null; newCaseTitle: string; documentTitle: string }) => void;
}) {
  /**
   * Что выбрал юрист. `undefined` — ещё не выбирал, и тогда подставляется первое
   * дело списка. Список приходит из базы и может доехать позже самого диалога:
   * возьми мы его один раз при появлении, дело подставилось бы «Новым» навсегда.
   */
  const [chosen, setChosen] = useState<string | null | undefined>(undefined);
  const caseId = chosen === undefined ? (cases[0]?.id ?? null) : chosen;

  const [newCaseTitle, setNewCaseTitle] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [picking, setPicking] = useState(false);

  const isNewCase = caseId === null;
  const canSave = !isSaving && (!isNewCase || newCaseTitle.trim().length > 0);

  return (
    <>
      <AppFormDialog
        title="Куда сохранить"
        onDismiss={onDismiss}
        confirm={{
          label: isSaving ? 'Сохраняем…' : 'Сохранить',
          enabled: canSave,
          onClick: () => onSave({ caseId, newCaseTitle, documentTitle }),
        }}
      >
        <AppPickerRow
          label="Дело"
          value={cases.find((item) => item.id === caseId)?.title ?? 'Новое дело'}
          onClick={() => setPicking(true)}
        />

        {isNewCase ? (
          <AppTextField value={newCaseTitle} onChange={setNewCaseTitle} label="Название дела" />
        ) : null}

        <AppTextField
          value={documentTitle}
          onChange={setDocumentTitle}
          label="Название документа"
        />
        <p className="add-document__hint">
          Без названия документ попадёт в дело как «Не разобрано»
        </p>
      </AppFormDialog>

      {picking ? (
        <AppMenuSheet
          title="Дело"
          onDismiss={() => setPicking(false)}
          items={[
            { icon: 'add', label: 'Новое дело', onSelect: () => setChosen(null) },
            ...cases.map((item) => ({
              icon: 'folder' as const,
              label: item.title,
              onSelect: () => setChosen(item.id),
            })),
          ]}
        />
      ) : null}
    </>
  );
}
